import { useEffect, useCallback, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { secureStorage } from '@/lib/security-utils';

/**
 * Hook de seguridad que valida constantemente que el usuario tenga acceso a la empresa seleccionada
 * Previene acceso no autorizado a datos de otras empresas
 */
export const useCompanySecurityValidator = () => {
  const { user, selectedCompany, userCompanies, signOut } = useAuth();
  const { toast } = useToast();
  const [isValidating, setIsValidating] = useState(false);
  const [lastValidationTime, setLastValidationTime] = useState<number>(0);

  // Validar acceso a la empresa actual
  const validateCurrentCompanyAccess = useCallback(async () => {
    if (!user || !selectedCompany || isValidating) return true;

    // No validar muy frecuentemente (cada 10 minutos máximo)
    const now = Date.now();
    if (now - lastValidationTime < 10 * 60 * 1000) return true;

    setIsValidating(true);
    try {
      
      // Crear un controller para cancelar la petición si es necesario
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 segundos timeout
      
      try {
        // Verificar que el usuario aún tenga roles activos para esta empresa
        const { data: activeRoles, error } = await supabase
          .from('user_roles')
          .select('id, is_active, company_id, role_id')
          .eq('user_id', user.id)
          .eq('company_id', selectedCompany.id)
          .eq('is_active', true)
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        if (error) {
          // Manejar AbortError específicamente (timeout)
          if (error.message?.includes('AbortError') || error.message?.includes('signal is aborted')) {
            console.warn('🕐 Validación cancelada por timeout, manteniendo sesión');
            return true;
          }
          
          console.error('Error validando permisos de empresa:', error);
          
          // Categorizar errores de red más específicamente
          const isNetworkError = error.message?.includes('fetch') || 
                                 error.message?.includes('timeout') ||
                                 error.message?.includes('network') ||
                                 error.message?.includes('Failed to fetch') ||
                                 error.message?.includes('ERR_CONNECTION_CLOSED') ||
                                 error.code === 'PGRST301' ||
                                 error.code === 'PGRST116';
          
          if (isNetworkError) {
            console.warn('Error de conexión temporal, manteniendo sesión');
            return true;
          }
          
          return false;
        }

        if (!activeRoles || activeRoles.length === 0) {
          console.warn('⚠️ Usuario perdió acceso a la empresa actual:', selectedCompany.name);
          
          // Mostrar alerta crítica
          toast({
            title: "⚠️ Acceso Revocado",
            description: `Tu acceso a "${selectedCompany.name}" ha sido revocado. Serás desconectado por seguridad.`,
            variant: "destructive",
            duration: 10000
          });

          // Limpiar localStorage con datos inválidos (usando secureStorage)
          secureStorage.removeItem(`lastCompany_${user.id}`);
          secureStorage.removeItem('selectedCompanyId'); 
          secureStorage.removeItem('selectedCompanyData');

          // Desconectar usuario por seguridad después de unos segundos
          setTimeout(() => {
            signOut();
          }, 3000);

          return false;
        }

        // Verificar que la empresa aún esté en la lista del usuario
        const companyStillAvailable = userCompanies.some(uc => uc.company.id === selectedCompany.id);
        if (!companyStillAvailable) {
          console.warn('⚠️ Empresa ya no está disponible en la lista del usuario');
          return false;
        }

        setLastValidationTime(now);
        console.log('✅ Acceso a empresa validado correctamente');
        return true;

      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        // Manejar errores específicos de fetch/network
        if (fetchError.name === 'AbortError') {
          console.warn('🕐 Validación cancelada por timeout, manteniendo sesión');
          return true;
        }
        
        const isNetworkError = fetchError.message?.includes('fetch') || 
                               fetchError.message?.includes('network') ||
                               fetchError.message?.includes('ERR_CONNECTION_CLOSED') ||
                               fetchError.toString().includes('TypeError: Failed to fetch');
        
        if (isNetworkError) {
          console.warn('🌐 Error de red detectado, manteniendo sesión:', fetchError.message);
          return true;
        }
        
        throw fetchError; // Re-lanzar otros errores
      }

    } catch (error: any) {
      console.error('Error en validación de seguridad:', error);
      
      // Para errores críticos no relacionados con la red, mantener la sesión por seguridad
      // Solo desconectar si es un error de autenticación específico
      const isAuthError = error.message?.includes('JWT') || 
                          error.message?.includes('auth') ||
                          error.code === 'PGRST301';
                          
      if (isAuthError) {
        console.error('🚨 Error de autenticación detectado, puede requerir relogin');
        return false;
      }
      
      // Para otros errores, mantener sesión pero reportar
      console.warn('⚠️ Error no crítico en validación, manteniendo sesión');
      return true;
      
    } finally {
      setIsValidating(false);
    }
  }, [user, selectedCompany, userCompanies, isValidating, lastValidationTime, toast, signOut]);

  // Validar integridad del secureStorage
  const validateLocalStorageIntegrity = useCallback(() => {
    if (!user || !selectedCompany) return;

    try {
      const savedCompanyId = secureStorage.getItem(`lastCompany_${user.id}`);
      const savedCompanyData = secureStorage.getItem('selectedCompanyData');

      if (savedCompanyData) {
        let companyData;
        
        // Intentar parsear como JSON normal primero
        try {
          companyData = JSON.parse(savedCompanyData);
        } catch (jsonError) {
          // Si falla el JSON normal, intentar desencriptar
          console.log('🔍 Intentando desencriptar datos de localStorage...');
          try {
            const decryptedData = secureStorage.getItem('selectedCompanyData', true);
            if (decryptedData && decryptedData.trim()) {
              companyData = JSON.parse(decryptedData);
            } else {
              console.warn('⚠️ No se pudo obtener datos desencriptados válidos');
              throw new Error('Datos desencriptados vacíos o inválidos');
            }
          } catch (decryptError) {
            console.warn('⚠️ Error al desencriptar o parsear datos, limpiando secureStorage...', decryptError);
            secureStorage.removeItem(`lastCompany_${user.id}`);
            secureStorage.removeItem('selectedCompanyData');
            secureStorage.removeItem('selectedCompanyId');
            return;
          }
        }
        
        // Verificar que tenemos datos válidos
        if (!companyData || typeof companyData !== 'object') {
          console.warn('⚠️ Datos de company inválidos en secureStorage, limpiando...');
          secureStorage.removeItem(`lastCompany_${user.id}`);
          secureStorage.removeItem('selectedCompanyData');
          secureStorage.removeItem('selectedCompanyId');
          return;
        }
        
        // Verificar que los datos guardados pertenecen al usuario actual
        if (companyData.userId && companyData.userId !== user.id) {
          console.warn('⚠️ Datos de secureStorage pertenecen a otro usuario, limpiando...');
          secureStorage.removeItem(`lastCompany_${user.id}`);
          secureStorage.removeItem('selectedCompanyData');
          secureStorage.removeItem('selectedCompanyId');
          return;
        }

        // Verificar que la empresa guardada coincide con la actual
        if (savedCompanyId !== selectedCompany.id) {
          console.warn('⚠️ Inconsistencia en secureStorage, actualizando...');
          secureStorage.setItem(`lastCompany_${user.id}`, selectedCompany.id);
          secureStorage.setItem('selectedCompanyId', selectedCompany.id);
        }
      }
    } catch (error) {
      console.error('Error validando integridad del secureStorage:', error);
      // Si hay error, limpiar secureStorage completamente
      try {
        secureStorage.removeItem(`lastCompany_${user.id}`);
        secureStorage.removeItem('selectedCompanyData');
        secureStorage.removeItem('selectedCompanyId');
      } catch (cleanupError) {
        console.error('Error limpiando secureStorage:', cleanupError);
      }
    }
  }, [user, selectedCompany]);

  // Efecto para validar periódicamente
  useEffect(() => {
    if (!user || !selectedCompany) return;

    // Delay inicial de 2 minutos para usuarios recién registrados
    // para permitir que se complete el proceso de asignación
    const initialDelay = setTimeout(() => {
      validateCurrentCompanyAccess();
      validateLocalStorageIntegrity();
    }, 120000); // 2 minutos delay inicial

    // Validación periódica cada 30 minutos después del delay inicial
    const validationInterval = setInterval(() => {
      validateCurrentCompanyAccess();
      validateLocalStorageIntegrity();
    }, 1800000); // 30 minutos

    return () => {
      clearTimeout(initialDelay);
      clearInterval(validationInterval);
    };
  }, [user?.id, selectedCompany?.id, validateCurrentCompanyAccess, validateLocalStorageIntegrity]);

  // Validar cuando la ventana recupera el foco (usuario regresa a la pestaña)
  // Optimizado con throttling para evitar validaciones excesivas
  useEffect(() => {
    if (!user || !selectedCompany) return;

    let focusTimeout: NodeJS.Timeout;
    let lastFocusTime = 0;

    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusTime;
      
      // Solo ejecutar validaciones si la ventana estuvo inactiva por más de 5 minutos
      if (timeSinceLastFocus > 300000) {
        console.log('👁️ Validando acceso tras inactividad prolongada...');
        
        if (focusTimeout) {
          clearTimeout(focusTimeout);
        }
        
        focusTimeout = setTimeout(() => {
          validateCurrentCompanyAccess();
          validateLocalStorageIntegrity();
        }, 100);
      }
      
      lastFocusTime = now;
    };

    const handleBlur = () => {
      lastFocusTime = Date.now();
    };

    window.addEventListener('focus', handleFocus, { passive: true });
    window.addEventListener('blur', handleBlur, { passive: true });
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }, [user, selectedCompany, validateCurrentCompanyAccess, validateLocalStorageIntegrity]);

  // Detectar cambios en localStorage de otras pestañas
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (!user) return;

      // Si otra pestaña modificó datos de empresa, validar
      if (e.key && (e.key.includes('company') || e.key.includes(user.id))) {
        console.log('🔍 Detectado cambio en localStorage de otra pestaña, validando...');
        setTimeout(() => {
          validateLocalStorageIntegrity();
          validateCurrentCompanyAccess();
        }, 1000);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user?.id, validateCurrentCompanyAccess, validateLocalStorageIntegrity]);

  return {
    validateCurrentCompanyAccess,
    isValidating,
    lastValidationTime
  };
};